import React, { useEffect, useRef, ReactNode, useMemo } from 'react';
import { drawInitialMap, cleanupMap } from 'utils/apple-maps';
import { useThemeContext } from 'contexts';
import { isClientSide } from 'utils/host-config';
import AppleMap from '@zoocasa/node-kit/map/apple/map';
import { ThemeNames } from 'types/themes';

export interface GeoJSONPoint {
  type: 'Point';
  coordinates: [number, number];
}

interface Point {
  type: 'Point';
  coordinates: number[];
}

interface BaseMapProps {
  position: GeoJSONPoint | Point;
  mapElementId: string;
  isInteractive?: boolean;
  renderPin?: boolean;
  shouldLoad?: boolean;
  address?: string;
  className?: string;
  children?: ReactNode;
  onMapLoaded?: (mapInstance: AppleMap) => void;
  onError?: (error: any) => void;
  'data-testid'?: string;
}

const BaseMap: React.FC<BaseMapProps> = ({
  position,
  mapElementId,
  isInteractive = true,
  renderPin = true,
  shouldLoad = true,
  address,
  className,
  children,
  onMapLoaded,
  onError,
  'data-testid': dataTestId,
}) => {
  const mapRef = useRef<AppleMap | null>(null);
  const { themeName } = useThemeContext();

  const stablePosition = useMemo(() => position, [
    position.coordinates[0], 
    position.coordinates[1],
  ]);

  useEffect(() => {
    if (shouldLoad && mapRef.current === null && isClientSide()) {
      const initializeMap = async () => {
        // Check if element exists and is visible
        const element = document.getElementById(mapElementId);
        if (!element) {
          return;
        }
        
        // Check if map is already being initialized (race condition check)
        if (element.dataset.initializing === 'true') {
          return;
        }
        
        // Mark as initializing to prevent race conditions
        element.dataset.initializing = 'true';
        
        try {
          const mapInstance = await drawInitialMap(
            mapElementId,
            stablePosition,
            isInteractive,
            themeName as ThemeNames,
            renderPin
          );

          mapRef.current = mapInstance;
          
          // Check if location needs to be adjusted
          const [longitude, latitude] = stablePosition.coordinates;
          if (mapRef.current.longitude === 0 || mapRef.current.latitude === 0) {
            mapRef.current.changeLocation({ lat: latitude, lng: longitude }, 14);
          }
          
          onMapLoaded?.(mapInstance);

        } catch (error) {
          console.error('BaseMap: Map initialization failed', error);
          onError?.(error);
        } finally {
          // Clear the initializing flag
          element.dataset.initializing = 'false';
        }
      };

      initializeMap();
    }

    return () => {
      if (mapRef.current) {
        mapRef.current?.removeEventListeners();
        cleanupMap(mapElementId);
        mapRef.current = null;
      }
    };
  }, [shouldLoad, mapElementId, stablePosition, isInteractive, themeName, renderPin, onMapLoaded, onError]);

  return (
    <div className="map-container">
      {/* SEO-friendly static content */}
      {address && (
        <address 
          itemScope 
          itemType="https://schema.org/Place"
          style={{ display: 'none' }}
        >
          <span itemProp="name">{address}</span>
          <div itemProp="geo" itemScope itemType="https://schema.org/GeoCoordinates">
            <meta itemProp="latitude" content={stablePosition.coordinates[1].toString()} />
            <meta itemProp="longitude" content={stablePosition.coordinates[0].toString()} />
          </div>
        </address>
      )}
      
      {/* Interactive map */}
      {children}
      <div id={mapElementId} className={className} data-testid={dataTestId} />
    </div>
  );
};

export default BaseMap;
export { type BaseMapProps }; 